import { mapActions, mapGetters } from "vuex";
import toast from "@/utils/toast";
import { cleanObject } from "./utils/utils";

export default {
  name: "CertificateReviewMain",

  components: {},
  data() {
    return {
      filters: {
        page: 1,
        pageSize: 10,
        order: "desc",
        field: "updated_at",
        search: "",
        learning_type: "structured_learning",
        idp_period_id: null,
        all: false,
        nik: null,
        training_scope: null,
        platform: null,
        training_method_id: null,
        training_type: null,
        status: "in_review",
      },
      filterUsers: {
        company: null,
        division: null,
        department: null,
        position: null,
        employee: null,
        search: null,
      },
      initialFilterOptions: null, // Store initial unfiltered options
      position: null,
      isLoading: false,
      isModalLoading: false,
      isLoadingSummary: false,
      _searchTimer: null,
      _employeeSearchTimer: null,
      showModal: false,
      modalData: null,
      idpPeriodOptions: [],
      activeFiltersCount: 0,
      isLoadingApprove: false,
      showConfirmationSubmitModal: false,
      isSubmitting: false,
      statusOptions: [
        { value: null, label: "All Status" },
        { value: "in_review", label: "Pending" },
        { value: "revision", label: "Revisi Note" },
        { value: "completed", label: "Approved" },
      ],
    };
  },

  computed: {
    ...mapGetters("certificateReview", [
      "idpLearnings",
      "summaryIdpLearning",
      "trainingMethods",
      "trainingTypes",
      "competencies",
      "filterUserOptions",
      "certificateApprovalReport",
    ]),
    ...mapGetters("users", ["profileData"]),

    // Merged options that preserve initial company list
    displayFilterOptions() {
      if (!this.initialFilterOptions) {
        return this.filterUserOptions;
      }
      
      return {
        company: this.initialFilterOptions.company,
        division: this.initialFilterOptions.division,
        department: this.initialFilterOptions.department,
        position: this.initialFilterOptions.position,
        employee: this.filterUserOptions.employee,
      };
    },

    showingFrom() {
      if (!this.idpLearnings.totalItems) return 0;
      return (this.filters.page - 1) * this.filters.pageSize + 1;
    },

    showingTo() {
      const to = this.filters.page * this.filters.pageSize;
      return to > this.idpLearnings.totalItems ? this.idpLearnings.totalItems : to;
    },
  },

  watch: {
    filters: {
      deep: true,
      handler(newFilters) {
        this.updateUrlQuery(newFilters);
      },
    },

    "filterUsers.company"(newVal, oldVal) {
      // Only trigger if value actually changed (not initial load)
      if (oldVal !== undefined) {
        // Reset child filters when parent changes or cleared
        this.filterUsers.division = null;
        this.filterUsers.department = null;
        this.filterUsers.position = null;
        this.filterUsers.employee = null;
        this.loadDataUsers();
      }
    },

    "filterUsers.division"(newVal, oldVal) {
      if (oldVal !== undefined) {
        // Reset child filters when parent changes or cleared
        this.filterUsers.department = null;
        this.filterUsers.position = null;
        this.filterUsers.employee = null;
        this.loadDataUsers();
      }
    },

    "filterUsers.department"(newVal, oldVal) {
      if (oldVal !== undefined) {
        // Reset child filters when parent changes or cleared
        this.filterUsers.position = null;
        this.filterUsers.employee = null;
        this.loadDataUsers();
      }
    },

    "filterUsers.position"(newVal, oldVal) {
      if (oldVal !== undefined) {
        // Reset child filter when parent changes or cleared
        this.filterUsers.employee = null;
        this.loadDataUsers();
      }
    },

    "filterUsers.employee"() {
      this.filters.nik = this.filterUsers.employee;
      this.filters.page = 1;

      this.loadData();

      if (this.filterUsers.employee) {
        this.loadSummaryLearning({ nik: this.filterUsers.employee });
      }
    },

    "filters.idp_period_id"() {
      if (!this.filters.nik) return;

      this.filters.page = 1;

      this.loadData();
    },

    "$route.query": {
      handler() {
        this.syncFiltersFromQuery();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("certificateReview", [
      "getIdpLearnings",
      "getStructuredLearningDetail",
      "getUserByFilters",
      "getSummaryIdpLearning",
      "getTrainingMethods",
      "getTrainingTypes",
      "getCompetencies",
      "resetIdpLearnings",
      "completeStructuredLearning",
      "reviseStructuredLearning",
    ]),

    showFilterModal() {
      this.$bvModal.show("modal-filter");
    },

    buildQueryFromFilters() {
      const raw = {
        page: this.filters.page,
        pageSize: this.filters.pageSize,
        order: this.filters.order,
        field: this.filters.field,
        search: this.filters.search,
        all: this.filters.all,
        learning_type: this.filters.learning_type,
        idp_period_id: this.filters.idp_period_id,
        training_scope: this.filters.training_scope,
        platform: this.filters.platform,
        training_method_id: this.filters.training_method_id,
        training_type: this.filters.training_type,
        status: this.filters.status,
        competency_id: this.filters.competency_id,
        nik: this.filters.nik,
      };

      return cleanObject(raw);
    },

    async loadDataUsers() {
      const payload = cleanObject({
        ...this.filterUsers,
        bibOnly: true,
        employeeLimit: this.filterUsers.search ? 50 : 25,
      });

      await this.getUserByFilters(payload);
    },

    onEmployeeSearch(search, loading) {
      clearTimeout(this._employeeSearchTimer);

      if (!search || search.length < 2) {
        this.filterUsers.search = null;
        this.loadDataUsers();
        return;
      }

      if (loading) loading(true);
      this._employeeSearchTimer = setTimeout(async () => {
        this.filterUsers.search = search;
        await this.loadDataUsers();
        if (loading) loading(false);
      }, 350);
    },

    async loadData() {
      try {
        this.isLoading = true;

        const query = this.buildQueryFromFilters();

        await this.getIdpLearnings(query);

        if (this.idpLearnings.page) this.filters.page = this.idpLearnings.page;
        if (this.idpLearnings.limit) this.filters.pageSize = this.idpLearnings.limit;
      } finally {
        this.isLoading = false;
      }
    },

    onPageChange(payload) {
      let page = null;
      if (typeof payload === "number") page = payload;
      else if (payload?.currentPage !== undefined) page = payload.currentPage;
      else if (payload?.currentPageNumber !== undefined) page = payload.currentPageNumber;
      if (page === null) return;
      this.filters.page = page;
      this.loadData();
    },

    onPageChangeFromBv(page) {
      this.filters.page = page;
      this.loadData();
    },

    onPerPageChange(payload) {
      let per = null;
      if (typeof payload === "number") per = payload;
      else if (payload?.currentPerPage !== undefined) per = payload.currentPerPage;
      else if (payload?.perPage !== undefined) per = payload.perPage;
      if (per === null) return;
      this.filters.pageSize = per;
      this.filters.page = 1;
      this.loadData();
    },

    onSortChange(params) {
      if (!Array.isArray(params) || params.length === 0) return;
      const { field, type } = params[0] || {};
      const isReset = type === "none";
      this.filters = {
        ...this.filters,
        field: isReset ? null : field,
        order: isReset ? null : type || "asc",
        page: 1,
      };
      this.loadData();
    },

    onSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.loadData();
      }, 500);
    },

    clearFilter() {
      this.filters = { ...this.filters, search: "", page: 1 };
      this.loadData();
    },

    updateUrlQuery(filters) {
      const query = cleanObject({
        ...this.$route.query,
        page: filters.page,
        pageSize: filters.pageSize,
        order: filters.order,
        field: filters.field,
        search: filters.search,
      });

      const current = JSON.stringify(this.$route.query);
      const next = JSON.stringify(query);

      if (current !== next) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    syncFiltersFromQuery() {
      const query = this.$route.query;
      const parsed = {
        page: Number(query.page) || 1,
        pageSize: Number(query.pageSize) || 10,
        order: query.order || null,
        field: query.field || null,
        search: query.search || "",
      };

      Object.keys(parsed).forEach((key) => {
        this.$set(this.filters, key, parsed[key]);
      });
    },

    handleComplete() {
      if (this.isOwnLearning(this.modalData)) {
        toast({ type: "error", title: "Error", message: "You cannot review your own certificate." });
        return;
      }
      this.showConfirmationSubmitModal = true;
    },

    async handleRevise() {
      if (!this.modalData?.id) return;
      if (this.isOwnLearning(this.modalData)) {
        toast({ type: "error", title: "Error", message: "You cannot review your own certificate." });
        return;
      }

      const result = await this.$swal({
        title: "Catatan Revisi",
        input: "textarea",
        inputLabel: "Tell the employee what needs to be revised",
        inputPlaceholder: "Write revision note...",
        inputAttributes: {
          maxlength: 1000,
        },
        showCancelButton: true,
        confirmButtonText: "Kirim Revisi",
        confirmButtonColor: "#e63757",
        inputValidator: (value) => {
          if (!value || !value.trim()) return "Revision note is required";
          return null;
        },
      });

      if (!result.isConfirmed) return;

      try {
        await this.reviseStructuredLearning({ id: this.modalData.id, note: result.value.trim() });
        toast({ type: "success", title: "Success", message: "Catatan revisi berhasil dikirim" });
        this.$bvModal.hide("structured-learning-modal");
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";
        toast({ type: "error", title: "Error", message: msg });
      } finally {
        this.loadData();
      }
    },

    async handleViewDetail(row) {
      this.isModalLoading = true;
      this.selectedId = row.id;
      this.showModal = true;

      const detail = await this.getStructuredLearningDetail({ id: row.id, query: { nik: row.employee_nik } });

      this.modalData = detail;

      this.$nextTick(() => {
        this.$bvModal.show("structured-learning-modal");
      });
      this.isModalLoading = false;
    },

    async completeLearning(id) {
      if (!id) return;
      if (this.isOwnLearning(this.modalData)) {
        toast({ type: "error", title: "Error", message: "You cannot review your own certificate." });
        return;
      }

      try {
        await this.completeStructuredLearning({ id });

        toast({ type: "success", title: "Success", message: "Pembelajaran berhasil diselesaikan" });
        this.showConfirmationSubmitModal = false;
        this.$bvModal.hide("structured-learning-modal");
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";
        toast({ type: "error", title: "Error", message: msg });
      } finally {
        this.loadData();
      }
    },

    async loadSummaryLearning({ nik }) {
      this.isLoadingSummary = true;

      const payload = {
        learning_type: "structured_learning",
        nik: nik || this.filters.nik,
      };

      await this.getSummaryIdpLearning(payload);

      this.isLoadingSummary = false;
    },

    isOwnLearning(row) {
      const currentNik = this.profileData?.nik || this.profileData?.employee_nik;
      const learningNik = row?.employee_nik || row?.nik || row?.idp?.nik || row?.employee?.nik;
      return currentNik && learningNik && String(currentNik) === String(learningNik);
    },

    applyFilters(appliedFilters) {
      const cleaned = cleanObject(appliedFilters);

      Object.entries(cleaned).forEach(([key, val]) => {
        this.$set(this.filters, key, val);
      });

      this.$set(this.filters, "page", 1);
      this.updateActiveFilterCount();
      this.loadData();

      this.$bvModal.hide("modal-filter");
    },

    updateActiveFilterCount() {
      const { training_scope, platform, training_method_id, training_type, competency_id, status } = this.filters;
      const vals = [training_scope, platform, training_method_id, training_type, competency_id, status];

      this.activeFiltersCount = vals.filter((x) => x !== null && x !== undefined && x !== "").length;
    },

    resetFilters() {
      const reset = {
        training_scope: null,
        platform: null,
        training_method_id: null,
        training_type: null,
        competency_id: null,
        status: "in_review",
        page: 1,
      };

      Object.entries(reset).forEach(([key, val]) => {
        this.$set(this.filters, key, val);
      });

      this.activeFiltersCount = 0;

      this.loadData();
    },

    clearSingleFilter(filterKey) {
      if (this.filters.hasOwnProperty(filterKey)) {
        this.$set(this.filters, filterKey, null);
        this.filters.page = 1;
        this.updateActiveFilterCount();
        this.loadData();
      }
    },
  },

  async mounted() {
    this.syncFiltersFromQuery();

    await Promise.all([
      this.loadData(),
      this.getTrainingMethods(),
      this.getTrainingTypes(),
      this.getCompetencies(),
      this.loadDataUsers(),
    ]);

    // Save initial unfiltered options
      this.initialFilterOptions = {
        company: [...(this.filterUserOptions.company || [])],
        division: [...(this.filterUserOptions.division || [])],
        department: [...(this.filterUserOptions.department || [])],
        position: [...(this.filterUserOptions.position || [])],
      };
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
    clearTimeout(this._employeeSearchTimer);
    this.resetIdpLearnings();
  },
};
